import { useState, useEffect, useMemo } from 'react';

import sortBy from 'lodash/sortBy';

import { useFiltersFieldsManagerContext } from '../FilterFieldsManager';
import { useFiltersDataManager } from '../FiltersDataManager';
import {
  useFiltersStateManager,
  isEmptyFilterValue,
  set,
} from '../FiltersStateManager';
import { useQueryListData } from './data/useQueryListData';

const FilterField = ({
  label,
  name,
  path,
  source,
  onError,
  dependsFields,
  getPathByName,
  normalizeValue,
  renderComponent,
  multiFields,
  lazy,
  suffix,
  disabled,
  disableEmpty,
  actions,
  serverSideSearch,
}) => {
  const [searchQuery, setSearchQuery] = useState();
  const { setFilterValue, setFilterValues, getFilterValue, getFilterValues } =
    useFiltersStateManager();

  const {
    registerFilter,
    unregisterFilter,
    isFilterTouched,
    isFilterPending,
    isFilterDependsResolved,
    onFilterTouch,
    onFilterChange,
    onFilterLoad,
    onFilterResolve,
    allDependsPendingAwait,
    screen,
  } = useFiltersDataManager();

  const { compared } = useFiltersFieldsManagerContext();

  const values = getFilterValues();

  const value = getFilterValue(path);

  const depends = useMemo(() => {
    return dependsFields?.map(({ path }) => path);
  }, [dependsFields]);

  const dependsParams = useMemo(() => {
    return dependsFields?.reduce((acc, { name, path }) => {
      const dependFilterValue = getFilterValue(path);

      return !isEmptyFilterValue(dependFilterValue)
        ? {
            ...acc,
            [name]: Array.isArray(dependFilterValue)
              ? sortBy(dependFilterValue)
              : dependFilterValue,
          }
        : acc;
    }, {});
  }, [dependsFields, getFilterValue]);

  useEffect(() => {
    if (!source) {
      return;
    }

    registerFilter(path, depends);

    return () => {
      unregisterFilter(path);
    };
  }, []);

  const filterTouched = isFilterTouched(path);

  const filterPending = isFilterPending(path);

  const filterDependsResolved = isFilterDependsResolved(path);

  const handleFilterTouch = () => onFilterTouch(path);

  const handleStartChange = () => onFilterChange(path);

  const handleFilterLoad = () => onFilterLoad(path);

  const handleFilterResolve = (data) => {
    const normalizedValue = normalizeValue?.(value, data);

    if (normalizeValue && normalizedValue !== value) {
      handleChange(null, normalizedValue);
    }

    onFilterResolve(path);
  };

  const handleChange = (_, value) => {
    if (multiFields) {
      setFilterValue(path, value);
    } else {
      setFilterValues(
        values.map((collection) => ({
          ...collection,
          [name]: value,
        }))
      );
    }

    if (source) {
      handleStartChange();
    }
  };

  const handleChangeMultiple = (_, params) => {
    const newValues = set(
      values,
      Object.keys(params).reduce(
        (acc, key) => ({
          ...acc,
          [getPathByName(key)]: params[key],
        }),
        {}
      )
    );

    setFilterValues(newValues);

    if (source) {
      handleStartChange();
    }
  };

  const { data, isLoading, isRefetching, refetch, error, isFetched } =
    useQueryListData({
      screen,
      name,
      source,
      dependsParams,
      value,
      onError,
      onStartLoad: handleFilterLoad,
      onEndLoad: handleFilterResolve,
      serverSideSearch,
    });

  const searchState = useQueryListData({
    screen,
    name,
    source,
    dependsParams,
    searchQuery,
    onError,
    enabled: serverSideSearch && searchQuery?.length > 0,
    serverSideSearch,
  });

  const empty = isEmptyFilterValue(value);

  useEffect(() => {
    if (!empty && !filterTouched) {
      handleFilterTouch();
    }
  }, [empty]);

  useEffect(() => {
    if (!source) {
      return;
    }

    if ((!empty || filterTouched || !lazy) && !isFetched) {
      refetch();
    }
  }, [path, empty, lazy, isFetched]);

  useEffect(() => {
    if (!source) {
      return;
    }

    if (filterPending && (filterDependsResolved || allDependsPendingAwait)) {
      if (!isFetched) {
        refetch();
      } else {
        handleFilterResolve(data);
      }
    }
  }, [filterPending, filterDependsResolved, allDependsPendingAwait, isFetched]);

  const handleFocus = () => {
    if (!source) {
      return;
    }

    if (!filterTouched) {
      handleFilterTouch();

      if (!data) {
        refetch();
      }
    }
  };

  const handleSearch = (_, value) => {
    setSearchQuery(value);
  };

  const handleClose = () => {
    setSearchQuery('');
  };

  const filterDisabled =
    disabled || filterPending || (disableEmpty && data?.length === 0);

  let bag = {
    label,
    value,
    options: data,
    onChange: handleChange,
    onChangeMultiple: handleChangeMultiple,
    onFocus: handleFocus,
    error: Boolean(error),
    compared,
    actions,
    values,
    suffix:
      typeof suffix === 'function'
        ? suffix({ disabled: filterDisabled })
        : suffix,
    disabled: filterDisabled,
  };
  if (serverSideSearch) {
    bag.searchOptions = searchQuery?.length > 0 ? searchState.data : undefined;
    bag.onSearch = handleSearch;
    bag.onClose = handleClose;
    bag.minOptionCountForSearch = 0;
    bag.postFiltering = false;
    bag.fetching = searchState.isRefetching || isRefetching;
    bag.loading = searchState.isLoading || isLoading;
  } else {
    bag.minOptionCountForSearch = 10;
    bag.postFiltering = true;
    bag.fetching = false;
    bag.loading = isLoading || isRefetching;
  }

  return renderComponent(bag);
};

export default FilterField;
